import type { Task } from "@src/domain/pascos/entities/task.entity";
import type { ITaskRepository, SearchTaskResult } from "@src/domain/pascos/repository/task_repository";
import { TaskStatus } from "@src/domain/pascos/value_objects/task_status";
import type { ApiClient } from "../api/client";
import { TaskMap } from "../mappers/task_mapper";
import { TaskDtoMapper } from "@src/application/tasks/mappers/task-dto.mapper";

export class TaskRepositoryImpl implements ITaskRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async getTaskStatus(taskId: string): Promise<Task> {
        const data = await this.apiClient.get<any>(`/tasks/${taskId}/status`);
        return TaskMap.toDomain(data);
    }

    async updateTaskStatus(taskId: string, status: TaskStatus): Promise<Task> {
        const data = await this.apiClient.put<any>(`/tasks/${taskId}/status`, {
            status,
        });
        return TaskMap.toDomain(data);
    }

    async listAll(): Promise<Task[]> {
        const data = await this.apiClient.get<any[]>("/tasks");

        // Ensure data is an array
        if (!Array.isArray(data)) {
            console.warn('API returned unexpected data structure for tasks list:', data);
            return [];
        }

        return data.map((t: any) => TaskMap.toDomain(t));
    }

    async listPaginated(params: {
        status?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchTaskResult> {
        const queryParams = new URLSearchParams();

        if (params.status) queryParams.append('status', params.status);
        if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());

        const data = await this.apiClient.get<any>(`/tasks?${queryParams.toString()}`);

        // Handle paginated response - be more defensive about the response structure
        let results: any[] = [];
        if (Array.isArray(data)) {
            results = data;
        } else if (data && typeof data === 'object') {
            results = data?.tasks || data?.results || data?.data || [];
        }

        // Ensure results is an array
        if (!Array.isArray(results)) {
            console.warn('API returned unexpected data structure for tasks:', data);
            results = [];
        }

        // Convert raw API data to Task entities first, then to DTOs
        const taskEntities = results.map((t: any) => TaskMap.toDomain(t));

        // Determine if there are more pages
        let hasMore = false;
        if (data?.has_more !== undefined) {
            hasMore = data.has_more;
        } else if (data?.hasMore !== undefined) {
            hasMore = data.hasMore;
        } else if (data?.page && data?.total_pages) {
            hasMore = data.page < data.total_pages;
        } else {
            // Fallback: if no pagination metadata, check if we got fewer results than requested
            // This indicates we've reached the end of the data
            hasMore = !(results.length < (params.limit || 20));
        }

        return {
            tasks: TaskDtoMapper.toListItemDTOArray(taskEntities),
            total: data?.total || results.length || 0,
            page: data?.page || Math.floor((params.skip || 0) / (params.limit || 20)) + 1,
            limit: data?.size || params.limit || 20,
            hasMore,
        };
    }

    async delete(taskId: string): Promise<void> {
        await this.apiClient.delete(`/tasks/${taskId}`);
    }
}
